<c-container class="mt-5 mb-5">
  <div class="mb-4">
    <h2 class="mat-headline-5 mb-2">{{ isEditMode ? 'Edit' : 'Create' }} Broadcast Notification</h2>
  </div>

  <button mat-raised-button
          class="margin-bottom-med margin-top-med"
          data-testid="back-to-manage-broadcast-notifications-button"
          routerLink="/manage-broadcast-notifications">
    <mat-icon>arrow_back_ios</mat-icon>
    Back to Manage Broadcast Notifications
  </button>

  <mat-card class="mat-elevation-z2">
    <mat-card-content class="p-4">
      <mat-stepper [linear]="true" #stepper class="mt-3">

        <!-- Step 1: Scope Selection -->
        <mat-step [stepControl]="targetFormGroup" label="Target" *ngIf="shouldShowTargetStep">
          <ng-template matStepLabel>Choose Target</ng-template>
          <form [formGroup]="targetFormGroup">
            <div class="step-content">
              <h5 class="mat-headline-6 text-secondary mb-4">Who should see this notification?</h5>

              <!-- Causable Admin: Can choose scope and or game -->
              <div *ngIf="isCausableAdmin">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Notification Scope</mat-label>
                  <mat-select formControlName="scope"
                              data-testid="broadcast-notification-scope-select"
                              placeholder="Choose notification scope"
                              (selectionChange)="onScopeChange()">
                    <mat-option [value]="BroadcastNotificationScopeTypes.System">System</mat-option>
                    <mat-option [value]="BroadcastNotificationScopeTypes.Game">Game</mat-option>
                  </mat-select>
                  <mat-hint>System notifications appear for all users, Game notifications for selected game only</mat-hint>
                  <mat-error *ngIf="targetFormGroup.get('scope')?.errors?.['required']">
                    Scope is required
                  </mat-error>
                </mat-form-field>

                <!-- Game Selection (Only when Game-Specific is selected) -->
                <div class="mt-3" *ngIf="targetFormGroup.get('scope')?.value === BroadcastNotificationScopeTypes.Game">
                  <app-choose-your-game
                    #gameSelector
                    [message]="'Choose which game this notification applies to'"
                    (gameChosenEvent)="onGameSelected($event)"
                    data-testid="broadcast-notification-game">
                  </app-choose-your-game>
                  <mat-error *ngIf="targetFormGroup.get('game')?.errors?.['required']">
                    Game selection is required for game-specific notifications
                  </mat-error>
                </div>
              </div>

              <!-- Non-Causable Admin -->
              <div *ngIf="!isCausableAdmin">
                <div class="full-width">
                  <app-choose-your-game
                    #gameSelector
                    [message]="'This notification will apply to the selected game only'"
                    (gameChosenEvent)="onGameSelected($event)"
                    data-testid="broadcast-notification-game">
                  </app-choose-your-game>
                  <mat-error *ngIf="targetFormGroup.get('game')?.errors?.['required']">
                    Game selection is required
                  </mat-error>
                </div>
              </div>
            </div>

            <mat-divider class="mt-4 mb-4"></mat-divider>
            <div class="d-flex justify-content-end">
              <button
                mat-button matStepperNext
                data-testid="target-step-next-button"
                [disabled]="!targetFormGroup.valid">Next</button>
            </div>
          </form>
        </mat-step>

        <!-- Step 2: Message Content -->
        <mat-step [stepControl]="messageFormGroup" label="Message">
          <ng-template matStepLabel>Write Message</ng-template>
          <form [formGroup]="messageFormGroup">
            <div class="step-content">
              <h5 class="mat-headline-6 text-secondary mb-4">What's your message?</h5>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Notification Title</mat-label>
                <input matInput
                       formControlName="title"
                       data-testid="broadcast-notification-title-input"
                       placeholder="Enter a brief title for your notification..."
                       maxlength="100">
                <mat-hint>{{ messageFormGroup.get('title')?.value?.length || 0 }}/100</mat-hint>
                <mat-error *ngIf="messageFormGroup.get('title')?.errors?.['required']">
                  Title is required
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Notification Message</mat-label>
                <textarea matInput
                          formControlName="message"
                          data-testid="broadcast-notification-message-input"
                          placeholder="Enter your notification message..."
                          rows="4"
                          maxlength="500"></textarea>
                <mat-hint>{{ messageFormGroup.get('message')?.value?.length || 0 }}/500</mat-hint>
                <mat-error *ngIf="messageFormGroup.get('message')?.errors?.['required']">
                  Message is required
                </mat-error>
              </mat-form-field>

              <div class="row mt-3">
                <div class="col-md-12">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Display Type</mat-label>
                    <mat-select formControlName="type"
                                data-testid="broadcast-notification-display-type"
                                placeholder="Choose display type">
                      <mat-option [value]="BroadcastNotificationTypes.Banner">Banner</mat-option>
                      <mat-option [value]="BroadcastNotificationTypes.Notification">Notification</mat-option>
                      <mat-option [value]="BroadcastNotificationTypes.TopStrip">Top Strip</mat-option>
                    </mat-select>
                    <mat-hint>How should this appear to users?</mat-hint>
                    <mat-error *ngIf="messageFormGroup.get('type')?.errors?.['required']">
                      Display type is required
                    </mat-error>
                  </mat-form-field>
                </div>
              </div>

              <!-- Color Selection -->
              <div class="mt-3">
                <div class="row">
                  <div class="col-md-6">
                    <label class="mat-label">Background Color (Optional)</label>
                    <div class="color-picker-container mt-2">
                      <ngx-colors ngx-colors-trigger class="color-picker-button"
                                  formControlName="backgroundColor"
                                  data-testid="broadcast-notification-color-picker"
                                  [acceptLabel]="'CONFIRM'"></ngx-colors>
                      <span class="color-display ms-2">{{ messageFormGroup.get('backgroundColor')?.value || 'Default' }}</span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="mat-label">Text Color (Optional)</label>
                    <div class="color-picker-container mt-2">
                      <ngx-colors ngx-colors-trigger class="color-picker-button"
                                  formControlName="textColor"
                                  data-testid="broadcast-notification-text-color-picker"
                                  [acceptLabel]="'CONFIRM'"></ngx-colors>
                      <span class="color-display ms-2">{{ messageFormGroup.get('textColor')?.value || 'Default' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <mat-divider class="mt-4 mb-4"></mat-divider>
            <div class="d-flex justify-content-between">
              <button mat-button matStepperPrevious>Back</button>
              <button
                mat-button matStepperNext
                data-testid="message-step-next-button"
                [disabled]="!messageFormGroup.valid">Next</button>
            </div>
          </form>
        </mat-step>

        <!-- Step 3: Schedule -->
        <mat-step [stepControl]="scheduleFormGroup" label="Schedule">
          <ng-template matStepLabel>Set Schedule</ng-template>
          <form [formGroup]="scheduleFormGroup">
            <div class="step-content">
              <h5 class="mat-headline-6 text-secondary mb-4">When should this notification be active?</h5>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="mat-label">Start Date & Time</label>
                    <input
                      class="mat-datetime-input full-width"
                      type="datetime-local"
                      formControlName="startDateTime"
                      data-testid="broadcast-notification-start-datetime"/>
                    <mat-error *ngIf="scheduleFormGroup.get('startDateTime')?.errors?.['required']">
                      Start date is required
                    </mat-error>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="mat-label">End Date & Time</label>
                    <input
                      class="mat-datetime-input full-width"
                      type="datetime-local"
                      formControlName="endDateTime"
                      data-testid="broadcast-notification-end-datetime"/>
                    <mat-error *ngIf="scheduleFormGroup.get('endDateTime')?.errors?.['required']">
                      End date is required
                    </mat-error>
                  </div>
                </div>
              </div>

              <div class="d-flex flex-wrap gap-3 mt-4">
                <mat-checkbox formControlName="isDismissible"
                              data-testid="broadcast-notification-dismissible">
                  Users can dismiss this notification
                </mat-checkbox>
              </div>
            </div>

            <mat-divider class="mt-4 mb-4"></mat-divider>
            <div class="d-flex justify-content-between">
              <button mat-button matStepperPrevious>Back</button>
              <button
                mat-button matStepperNext
                data-testid="schedule-step-preview-button"
                [disabled]="!targetFormGroup.valid">Preview</button>
            </div>
          </form>
        </mat-step>

        <!-- Step 4: Preview & Create -->
        <mat-step label="Preview">
          <ng-template matStepLabel>{{ isEditMode ? 'Review & Update' : 'Review & Create' }}</ng-template>
          <div class="step-content">
            <h5 class="mat-headline-6 text-secondary mb-4">Preview Your Notification</h5>

            @if (previewNotification) {
              <app-broadcast-notification-preview
                [notification]="previewNotification"
                [gameName]="selectedGameName">
              </app-broadcast-notification-preview>
            }
          </div>

          <mat-divider class="mt-4 mb-4"></mat-divider>
          <div class="d-flex justify-content-between flex-wrap gap-2">
            <button mat-button matStepperPrevious>Back</button>
            <div class="d-flex gap-2">
              <button mat-stroked-button (click)="resetForm()">
                {{ isEditMode ? 'Reset Changes' : 'Start Over' }}
              </button>
              <button mat-flat-button
                      (click)="createBroadcastNotification()"
                      [disabled]="uiState === uiStateForTemplate.ShowLoading"
                      color="primary"
                      data-testid="create-broadcast-notification-button">
                {{ uiState === uiStateForTemplate.ShowLoading
                ? (isEditMode ? 'Updating...' : 'Creating...')
                : (isEditMode ? 'Update Notification' : 'Create Notification') }}
              </button>
            </div>
          </div>
        </mat-step>

      </mat-stepper>
    </mat-card-content>
  </mat-card>
</c-container>
